const dateFmt = new Intl.DateTimeFormat(undefined, { year: "numeric", month: "short", day: "numeric" });
const dateTimeFmt = new Intl.DateTimeFormat(undefined, {
  year: "numeric",
  month: "short",
  day: "numeric",
  hour: "numeric",
  minute: "2-digit",
});
const relFmt = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });

export function formatDate(value?: string | null): string {
  return value ? dateFmt.format(new Date(value)) : "—";
}

export function formatDateTime(value?: string | null): string {
  return value ? dateTimeFmt.format(new Date(value)) : "—";
}

export function formatRelative(value?: string | null, now: number = Date.now()): string {
  if (!value) return "—";
  const seconds = Math.round((new Date(value).getTime() - now) / 1000);
  const units: Array<[Intl.RelativeTimeFormatUnit, number]> = [
    ["year", 31_536_000],
    ["month", 2_592_000],
    ["week", 604_800],
    ["day", 86_400],
    ["hour", 3_600],
    ["minute", 60],
  ];
  for (const [unit, size] of units) {
    if (Math.abs(seconds) >= size) return relFmt.format(Math.round(seconds / size), unit);
  }
  return relFmt.format(seconds, "second");
}

export function initials(name: string): string {
  const parts = name
    .trim()
    .split(/\s+/)
    .map((part) => part.replace(/[^\p{L}\p{N}]/gu, ""))
    .filter(Boolean);
  if (parts.length === 0) return "?";
  return (parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : "")).toUpperCase();
}

export function pluralize(count: number, singular: string, plural = `${singular}s`): string {
  return `${count} ${count === 1 ? singular : plural}`;
}

/**
 * A calendar date from the API ("2026-11-03": an effective date, not an instant). Parsed as a local
 * date, so it never shifts a day the way `new Date("2026-11-03")` (UTC midnight) does west of UTC.
 */
export function formatCalendarDate(value?: string | null): string {
  if (!value) return "—";
  const [y, m, d] = value.split("-").map(Number);
  return dateFmt.format(new Date(y, m - 1, d));
}

const moneyFmts = new Map<string, Intl.NumberFormat>();

/** Money with 2 to 4 decimals ("38.5000" -> "$38.50", "0.1250" -> "$0.125"). One formatter per currency. */
export function formatMoney(value: string | number, currency = "USD"): string {
  let fmt = moneyFmts.get(currency);
  if (!fmt) {
    fmt = new Intl.NumberFormat(undefined, { style: "currency", currency, minimumFractionDigits: 2, maximumFractionDigits: 4 });
    moneyFmts.set(currency, fmt);
  }
  return fmt.format(Number(value));
}

/** An amount the API sent, for editing: no trailing zeros beyond cents ("175.0000" -> "175.00", "0.1250" -> "0.125"). */
export function moneyInput(value: string | null | undefined): string {
  if (value === null || value === undefined || value === "") return "";
  const [whole, decimals = ""] = value.split(".");
  return `${whole}.${decimals.replace(/0+$/, "").padEnd(2, "0")}`;
}
